import { computed, ref } from 'vue';
import { usePopups } from '@mm-frontend/mm-web-ui-kit';
import CharacteristicsModal from '../ui/mobile/CharacteristicsModal.vue';
import DescriptionModal from '../ui/mobile/DescriptionModal.vue';
import FeedbackModal from '../ui/mobile/FeedbackModal.vue';
import SortFeedbackModal from '../ui/mobile/SortFeedbackModal.vue';
import TabModal from '../ui/mobile/TabModal.vue';
import BnplModal from '../ui/common/bnpl-modal/bnpl-modal.vue';
import ProductGalleryPopup from '../ui/common/ProductGalleryPopup.vue';
import type { ICharacteristics } from '../models/characteristic';
import type { IProductOptionInfo } from '@/pages/index/-src/shared/types/product';
import type { IPhoto } from '../models/slider';
import { FeedbackSortType, FeedbackFilterType } from '@/shared/apollo-api/__generated__/types';

// Создаем модалки
const { createPopup } = usePopups();
const characteristicsModal = createPopup({
  component: CharacteristicsModal,
});

const descriptionModal = createPopup({
  component: DescriptionModal,
});

const feedbackModal = createPopup({
  component: FeedbackModal,
});

const sortFeedbackModal = createPopup({
  component: SortFeedbackModal,
});

const tabModal = createPopup({
  component: TabModal,
});

const bnplModal = createPopup({
  component: BnplModal,
});

const galleryPopup = createPopup({
  component: ProductGalleryPopup,
});

// Состояние модалки
const characteristicsData = ref<ICharacteristics | undefined>(undefined);

// Хранилище обработчика закрытия галереи
let galleryCloseCallback: ((activeIndex: number) => void) | null = null;

// Обработчик закрытия галереи
galleryPopup.onClose((data: { activeIndex: number } | undefined) => {
  if (data && galleryCloseCallback) {
    galleryCloseCallback(data.activeIndex);
    galleryCloseCallback = null;
  }
});

// Настройка модалки
characteristicsModal.onShow(() => {
  // Модалка открыта
});

characteristicsModal.onClose(() => {
  characteristicsData.value = undefined;
});

export function useProductModals() {
  /**
   * Открыть модалку характеристик
   */
  const openCharacteristicsModal = (characteristics: ICharacteristics) => {
    characteristicsData.value = characteristics;
    characteristicsModal.show({ characteristics });
  };

  /**
   * Закрыть модалку характеристик
   */
  const closeCharacteristicsModal = () => {
    characteristicsModal.close();
  };

  /**
   * Открыть модалку описания
   * @param description - текст описания
   */
  const openDescriptionModal = (description: string) => {
    descriptionModal.show({ description });
  };

  /**
   * Закрыть модалку описания
   */
  const closeDescriptionModal = () => {
    descriptionModal.close();
  };

  /**
   * Открыть модалку отзывов
   * @param productId - ID товара
   * @param feedbackQuantity - количество отзывов
   * @param rating - рейтинг товара
   */
  const openFeedbackModal = (productId: number, feedbackQuantity: number, rating: number) => {
    feedbackModal.show({ productId, feedbackQuantity, rating });
  };

  /**
   * Закрыть модалку отзывов
   */
  const closeFeedbackModal = () => {
    feedbackModal.close();
  };

  /**
   * Открыть модалку сортировки отзывов
   * @param currentSort - текущая сортировка
   * @param currentFilters - текущие фильтры
   */
  const openSortFeedbackModal = (
    currentSort: FeedbackSortType,
    currentFilters: FeedbackFilterType[]
  ) => {
    sortFeedbackModal.show({ currentSort, currentFilters });
  };

  /**
   * Закрыть модалку сортировки отзывов
   */
  const closeSortFeedbackModal = () => {
    sortFeedbackModal.close();
  };

  // Хранилище обработчика применения сортировки
  let sortApplyCallback: ((sort: FeedbackSortType, filters: FeedbackFilterType[]) => void) | null =
    null;

  // Обработчик закрытия модалки сортировки
  sortFeedbackModal.onClose(
    (data: { sort: FeedbackSortType; filters: FeedbackFilterType[] } | undefined) => {
      if (data && sortApplyCallback) {
        sortApplyCallback(data.sort, data.filters);
        sortApplyCallback = null;
      }
    }
  );

  /**
   * Установить обработчик применения сортировки
   */
  const setSortApplyHandler = (
    callback: (sort: FeedbackSortType, filters: FeedbackFilterType[]) => void
  ) => {
    sortApplyCallback = callback;
  };

  /**
   * Открыть модалку таба (Размеры, Инструкция, Состав, Сертификация и т.д.)
   * @param title - заголовок таба
   * @param content - HTML контент таба
   */
  const openTabModal = (title: string, content: string) => {
    tabModal.show({ title, content });
  };

  /**
   * Закрыть модалку таба
   */
  const closeTabModal = () => {
    tabModal.close();
  };

  /**
   * Открыть модалку BNPL (Долями/Подели)
   * @param optionInfo - информация об опции оплаты
   */
  const openBnplModal = (optionInfo: IProductOptionInfo) => {
    bnplModal.show({ optionInfo });
  };

  /**
   * Закрыть модалку BNPL
   */
  const closeBnplModal = () => {
    bnplModal.close();
  };

  /**
   * Открыть галерею изображений товара
   * @param photos - массив фотографий
   * @param index - индекс текущей фотографии
   * @param alt - альтернативный текст для изображений
   * @param onClose - callback при закрытии галереи
   */
  const openGalleryPopup = (
    photos: IPhoto[],
    index: number,
    alt: string,
    onClose?: (activeIndex: number) => void
  ) => {
    if (onClose) {
      galleryCloseCallback = onClose;
    }
    galleryPopup.show({ photos, index, alt });
  };

  /**
   * Закрыть галерею изображений товара
   */
  const closeGalleryPopup = () => {
    galleryPopup.close();
  };

  /**
   * Установить обработчик закрытия галереи
   */
  const setGalleryCloseHandler = (callback: (activeIndex: number) => void) => {
    galleryCloseCallback = callback;
  };

  return {
    // Методы для характеристик
    openCharacteristicsModal,
    closeCharacteristicsModal,

    // Методы для описания
    openDescriptionModal,
    closeDescriptionModal,

    // Методы для отзывов
    openFeedbackModal,
    closeFeedbackModal,

    // Методы для сортировки отзывов
    openSortFeedbackModal,
    closeSortFeedbackModal,
    setSortApplyHandler,

    // Методы для табов
    openTabModal,
    closeTabModal,

    // Методы для BNPL
    openBnplModal,
    closeBnplModal,

    // Методы для галереи
    openGalleryPopup,
    closeGalleryPopup,
    setGalleryCloseHandler,
  };
}
